import type { ReactNode, RefObject } from 'react'

import { useEffect, useId, useMemo, useState } from 'react'

import type { VuiComponent } from '../core'
import type { SystemProps } from '../system'
import type { ChangeEvent, PropsOf } from '../utils'
import type { TextFieldProps } from './textField.types'

import Box from '../box'
import { IconButton } from '../button'
import { styled, useStyleConfig, vui } from '../core'
import { T } from '../t'
import { cs, filterUndefined, isString } from '../utils'
import { clearIconSize, containerGap, displayValueOnlyTextSize, fieldColors, helpRowGap } from './consts'
import { TextFieldProvider } from './context'
import {
  clampValue,
  createSyntheticChangeEvent,
  filterAutoCompleteOption,
  getInitialCount,
  resolveLabel,
} from './helpers'
import TextFieldAutoComplete from './textFieldAutoComplete'
import TextFieldHelpText from './textFieldHelpText'
import TextFieldIcon from './textFieldIcon'

export const TextFieldControlBase = styled.divBox<{ $multiline?: boolean }>`
  align-items: center;
  color: ${fieldColors.inputText};
  background-color: white;
  border-radius: none;
  border-width: 1px;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  outline: none;
  padding: 0 8px;
  position: relative;
  transform: translate3d(0, 0, 0);
  transition-duration: 0s;
  width: 100%;

  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${fieldColors.disabledBackground};
    border-color: ${fieldColors.disabledBackground};
    color: ${fieldColors.disabledForeground};
    & > .vui-icon {
      color: ${fieldColors.disabledForeground};
    }
    & > .vui-icon > path {
      fill: ${fieldColors.disabledForeground};
    }
  }

  /* nested adornment buttons (itemLeft/itemRight, clear) rely on this container's own focus-within ring */
  & button:focus-visible {
    --x-ring-color: transparent;
    outline: none;
  }

  ${({ $multiline }) =>
    $multiline &&
    `
    align-items: stretch;
    flex-direction: column;
    gap: 0;
    padding: 0;
  `}
`

export const TextFieldInputBase = styled.input`
  align-self: stretch;
  background-color: transparent;
  border: none;
  border-radius: none;
  color: inherit;
  flex: 1;
  font-size: inherit;
  min-width: 0;
  outline: none;

  &[aria-disabled='true'],
  &[aria-disabled='true']::placeholder {
    cursor: not-allowed;
  }
`

export const TextFieldTextareaBase = styled.textareaBox`
  background-color: transparent;
  border: none;
  color: inherit;
  flex: 1 1 auto;
  font-size: inherit;
  outline: none;
  padding: 4px;
  resize: none;
  width: 100%;

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`

type TextFieldFooterProps = {
  count: number
  descriptionId?: string
  errorText?: ReactNode
  helpText?: ReactNode
  maxLength?: number
  showCount?: boolean
}

/** Renders the help/error text alongside the character counter in a single row. */
function TextFieldFooter({ count, descriptionId, errorText, helpText, maxLength, showCount }: TextFieldFooterProps) {
  if (!helpText && !errorText && !showCount) return null

  // errorText overrides helpText; both share the same slot
  const message = errorText ?? helpText
  const isCountOverMax = !!maxLength && count > maxLength

  return (
    <Box alignItems="flex-start" gap={helpRowGap}>
      <Box flex="1">
        {message && (
          <TextFieldHelpText id={descriptionId} isError={!!errorText}>
            {message}
          </TextFieldHelpText>
        )}
      </Box>
      {showCount && (
        <TextFieldHelpText className="vui-textFieldCount" isError={isCountOverMax}>
          {count} {maxLength ? `/ ${maxLength}` : null}
        </TextFieldHelpText>
      )}
    </Box>
  )
}

/**
 * Displays a text field wrapped in a div to allow extra content, like counter, side icons or buttons.
 * Renders a native `<input>` when `rows` is unset or `<= 1`, and a `<textarea>` when `rows > 1`.
 * Forwards many relevant props to the inner field. Handles different states, like loading or error.
 * Exposes some props to the children via context.
 */
export const TextField = vui<'div', TextFieldProps>((props, ref) => {
  const {
    // identity & native attributes
    ariaLabel,
    autoComplete,
    autoFocus,
    className,
    id: idProp,
    name,
    disabled,
    readOnly,
    required,

    // value & change handling
    defaultValue,
    value,
    onChange: onChangeProp,
    onBlur,
    onFocus,

    // native field constraints
    max,
    maxLength,
    min,
    minLength,
    pattern,
    placeholder,
    step,
    type = 'text',

    // multiline-only
    cols,
    resize,
    rows,

    // label
    label,
    labelMarkOptional,
    labelTooltipText,

    // help/error/loading/counter states
    helpText,
    errorText,
    loading,
    showCount,
    displayValueOnly,

    // adornments (input-only)
    allowClear,
    startIcon,
    itemLeft,
    itemRight,

    // autocomplete (input-only)
    autoCompleteOptions,
    autoCompleteMaxHeight,

    // custom field override
    field,
    fieldProps,
    fieldRef,

    // sizing/theming
    size = 'lg',
    variant,

    ...rest
  } = props

  const isMultiline = (rows ?? 0) > 1

  const generatedId = useId()
  const [count, setCount] = useState(() => getInitialCount(props))
  const [valueInternal, setValueInternal] = useState(value ?? defaultValue ?? '')
  // caps the resize handle to the textarea's `rows`-driven height plus its padding/border
  const minHeight = isMultiline && resize && rows ? `calc(${rows} * 1lh + 10px)` : undefined
  // errorText forces the red variant; loading forces the grey variant
  const effectiveVariant = errorText ? 'red' : loading ? 'grey' : variant

  const id = idProp || generatedId

  const styles = useStyleConfig('Input', { ...props, variant: effectiveVariant })
  // the theme's fixed `h` is single-line only; multiline height comes from the native `rows` attribute
  const { h: _containerHeight, ...containerStylesMultiline } = styles.container ?? {}
  const containerStyles = isMultiline ? containerStylesMultiline : styles.container

  // sub-components (Icon, HelpText) resolve styles from the same effective variant as the container,
  // so error/loading overrides are reflected in their styling too
  const context = useMemo(
    () => filterUndefined({ disabled, id, size, variant: effectiveVariant }),
    [disabled, id, size, effectiveVariant],
  )

  function onChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const value = clampValue(e.target.value, maxLength)

    setValueInternal(value)
    setCount(value?.length)
    // keep the forwarded event consistent with the clamped internal state, so a controlled
    // parent reading `e.target.value` can't diverge from what the field displays
    if (value !== e.target.value) {
      e.target.value = value
    }
    onChangeProp?.(e)
  }

  function onAutoCompleteSelect(value: string) {
    onChange(createSyntheticChangeEvent(value, 'autoCompleteSelect') as unknown as ChangeEvent<HTMLInputElement>)
  }

  function onClear() {
    setValueInternal('')
    setCount(0)
    onChange(createSyntheticChangeEvent('', 'onClear') as unknown as ChangeEvent<HTMLInputElement>)
  }

  const aliasedProps = filterUndefined({
    'aria-disabled': disabled,
    // disabled overrides everything (state, readOnly); readOnly only overrides when there's no active state
    bg: disabled ? fieldColors.disabledBackground : readOnly ? fieldColors.readOnlyBackground : undefined,
    borderColor: disabled
      ? fieldColors.disabledBackground
      : readOnly && !loading
        ? fieldColors.readOnlyBorder
        : undefined,
  })

  const fieldAliasedProps = filterUndefined({
    'aria-disabled': disabled,
    'aria-label': ariaLabel,
  })

  // link the field to its help/error text so screen readers announce it; errorText overrides helpText
  const descriptionId = helpText || errorText ? `${id}-description` : undefined

  const commonFieldProps = {
    value: valueInternal,
    ...styles.field,
    ...fieldAliasedProps,
    'aria-describedby': descriptionId,
    'aria-invalid': errorText ? true : undefined,
  }

  // valid on both <input> and <textarea>
  const sharedFieldProps = {
    autoFocus,
    disabled,
    id,
    maxLength,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    required,
  }

  const textareaFieldProps = {
    ...sharedFieldProps,
    cols,
    rows,
    ...commonFieldProps,
    ...(fieldProps as PropsOf<'textarea', SystemProps>),
  }

  const inputFieldProps = {
    ...sharedFieldProps,
    max,
    min,
    minLength,
    pattern,
    step,
    type,
    autoComplete: autoCompleteOptions?.length ? 'off' : autoComplete,
    ...commonFieldProps,
    ...(fieldProps as PropsOf<'input', SystemProps>),
  }

  useEffect(() => {
    if (value === undefined && defaultValue !== undefined) return
    setValueInternal(value ?? '')
    setCount(value?.toString()?.length ? `${value}`.length : 0)
  }, [value])

  const filterAutoCompleteOptions = (i: string) => filterAutoCompleteOption(`${valueInternal}`, i)

  const resolvedLabel = resolveLabel(label, id, disabled, labelMarkOptional, labelTooltipText, styles.label)

  // hidden type needs no visible container; render only the native input, but still forward
  // refs, props and change handling so it behaves like any other TextField
  if (type === 'hidden') {
    return (
      <input
        defaultValue={defaultValue}
        id={id}
        name={name}
        onChange={onChange}
        ref={fieldRef as RefObject<HTMLInputElement | null> | undefined}
        type="hidden"
        value={valueInternal as string}
        {...(fieldProps as PropsOf<'input', SystemProps>)}
      />
    )
  }

  return (
    <TextFieldProvider value={context}>
      {displayValueOnly ? (
        <T className={cs('vui-textField-value', className)} size={displayValueOnlyTextSize[size]}>
          {value || defaultValue}
        </T>
      ) : (
        <TextFieldAutoComplete
          autoCompleteMaxHeight={autoCompleteMaxHeight}
          autoCompleteOptions={isMultiline ? undefined : autoCompleteOptions}
          filterAutoCompleteOptions={filterAutoCompleteOptions}
          onAutoCompleteSelect={onAutoCompleteSelect}
        >
          <Box className="vui-textFieldContainer" column gap={containerGap} ref={ref} {...rest}>
            {resolvedLabel}

            <TextFieldControlBase
              $multiline={isMultiline}
              className={cs('vui-textField', className)}
              minH={isMultiline && resize ? minHeight : undefined}
              overflow={isMultiline && resize ? 'auto' : undefined}
              resize={isMultiline && resize ? 'vertical' : undefined}
              {...containerStyles}
              {...aliasedProps}
            >
              {!isMultiline && (
                <>
                  {itemLeft}
                  {isString(startIcon) ? <TextFieldIcon name={startIcon} /> : startIcon}
                </>
              )}
              {field ??
                (isMultiline ? (
                  <TextFieldTextareaBase className="vui-textFieldField" ref={fieldRef} {...textareaFieldProps} />
                ) : (
                  <TextFieldInputBase className="vui-textFieldField" ref={fieldRef} {...inputFieldProps} />
                ))}
              {!isMultiline && (
                <>
                  {itemRight}
                  {loading && (
                    <TextFieldIcon
                      animation="vui-spin 0.6s linear infinite"
                      name="uiSpinnerThird"
                      pathFill={[fieldColors.loading, fieldColors.loadingBar]}
                    />
                  )}
                  {allowClear && !!valueInternal && (
                    <IconButton
                      icon="uiTimes"
                      onClick={onClear}
                      size={clearIconSize[size]}
                      title="Clear"
                      variant="tertiary"
                    />
                  )}
                </>
              )}
            </TextFieldControlBase>
            <TextFieldFooter
              count={count}
              descriptionId={descriptionId}
              errorText={errorText}
              helpText={helpText}
              maxLength={maxLength}
              showCount={showCount}
            />
          </Box>
        </TextFieldAutoComplete>
      )}
    </TextFieldProvider>
  )
}) as VuiComponent<'div', TextFieldProps> & {
  AutoComplete: typeof TextFieldAutoComplete
  HelpText: typeof TextFieldHelpText
  Icon: typeof TextFieldIcon
}

TextField.AutoComplete = TextFieldAutoComplete
TextField.HelpText = TextFieldHelpText
TextField.Icon = TextFieldIcon
TextField.displayName = 'TextField'

export default TextField
